import { SHIFTS } from "@/constants/shifts";

/**
 * Matična firma i njene poslovnice u štampi liste za pakovanje (čista logika, bez Vue-a).
 * Izveštaj /obrok vraća firme po nazivu (bez ID-a), pa se veza sa matičnom firmom traži u spisku firmi
 * (`directory`: { id, name, parentId }) preko naziva. Naziv koji se u spisku ponavlja se ne grupiše.
 */

const slot = () => ({
  customerQuantity: 0,
  restaurantQuantity: 0,
  totalQuantity: 0,
  customerQuantityPayed: 0,
  restaurantQuantityPayed: 0,
  totalQuantityPayed: 0,
});

/**
 * Firme izveštaja u jedinice štampe, redom prvog pojavljivanja:
 * `{ type: "single", company }` ili `{ type: "group", name, members }` (matična firma + poslovnice koje
 * imaju obroke; matična firma je prva). Grupa ima najmanje dva člana.
 * @param {Array} companies firme izveštaja (imaju `name`)
 * @param {Array} directory { id, name, parentId } iz spiska firmi
 * @param {Function} norm normalizacija naziva (npr. toLatin)
 */
export function buildPackingUnits(companies, directory = [], norm = (text) => text) {
  const key = (name) => norm(String(name || "")).trim().toLowerCase();
  const byName = new Map();
  directory.forEach((entry) => {
    const k = key(entry.name);
    byName.set(k, byName.has(k) ? null : entry);
  });
  const byId = new Map(directory.map((entry) => [entry.id, entry]));

  const placed = companies.map((company) => {
    const entry = byName.get(key(company.name)) || null;
    const parentId = entry ? (entry.parentId ?? (isParent(entry, directory) ? entry.id : null)) : null;
    return { company, entry, parentId };
  });

  const groups = new Map();
  placed.forEach((item) => {
    if (item.parentId != null) {
      groups.set(item.parentId, [...(groups.get(item.parentId) || []), item]);
    }
  });

  const units = [];
  const done = new Set();
  placed.forEach((item) => {
    const members = item.parentId != null ? groups.get(item.parentId) : null;
    if (!members || members.length < 2) {
      units.push({ type: "single", company: item.company });
      return;
    }
    if (done.has(item.parentId)) {
      return;
    }
    done.add(item.parentId);
    const ordered = [...members].sort((a, b) => Number(Boolean(a.entry.parentId)) - Number(Boolean(b.entry.parentId)));
    const parent = byId.get(item.parentId);
    units.push({
      type: "group",
      name: parent ? parent.name : ordered[0].company.name,
      members: ordered.map((member) => member.company),
    });
  });
  return units;
}

function isParent(entry, directory) {
  return directory.some((other) => other.parentId === entry.id);
}

/** Jedna tabela od više firmi: jela se sabiraju po ID-u (ili nazivu), po smenama i u ukupnom. */
export function sumPackingCompanies(members, name) {
  const meals = new Map();
  members.forEach((company) => {
    (company.meals || []).forEach((meal) => {
      const key = meal.id ?? meal.name;
      if (!meals.has(key)) {
        const merged = { ...meal, total: 0 };
        SHIFTS.forEach((shift) => {
          merged[shift.key] = slot();
        });
        meals.set(key, merged);
      }
      const target = meals.get(key);
      SHIFTS.forEach((shift) => {
        const from = meal[shift.key];
        if (!from) {
          return;
        }
        Object.keys(slot()).forEach((field) => {
          target[shift.key][field] += from[field] || 0;
        });
      });
      target.total += meal.total || 0;
    });
  });

  const rows = [...meals.values()].map((meal, index) => ({ ...meal, num: index + 1 }));
  const totals = { total: 0 };
  SHIFTS.forEach((shift) => {
    totals[shift.key] = slot();
    rows.forEach((meal) => {
      Object.keys(slot()).forEach((field) => {
        totals[shift.key][field] += meal[shift.key][field];
      });
    });
  });
  rows.forEach((meal) => {
    totals.total += meal.total;
  });
  return { name, meals: rows, totals };
}
